import { fn } from '@ember/helper';
import { service } from '@ember/service';
import type Model from '@ember-data/model';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import { and } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import ConfirmationModal from 'garaje/components/ui/classic/confirmation-modal';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type ConnectLocationConfigurationModel from 'garaje/models/connect-location-configuration';
import type ZoneModel from 'garaje/models/zone';
import type FlashMessagesService from 'garaje/services/flash-messages';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import type { KeysMatching, ModelData } from 'garaje/utils/type-utils';

type SettingsModels = ZoneModel | ConnectLocationConfigurationModel;
type BooleanKeys<T extends Model> = KeysMatching<ModelData<T>, boolean>;

interface RebadgingSettingsComponentArgs<T extends SettingsModels> {
  model: T;
  toggleKey: BooleanKeys<T>;
  title: string;
  description: string;
  showDisableConfirmation?: boolean;
  confirmationMessage?: string;
  cannotToggleLink?: string;
  cannotToggleLinkText?: string;
}

export default class RebadgingSettingsComponent<T extends SettingsModels> extends Component<
  RebadgingSettingsComponentArgs<T>
> {
  @tracked showConfirmationModal = false;

  get isChecked(): boolean {
    return Boolean(this.args.model[this.args.toggleKey]);
  }

  @service declare flashMessages: FlashMessagesService;

  toggleTask = task({ drop: true }, async (confirmed = false) => {
    if (confirmed) this.showConfirmationModal = false;

    const newToggleValue = <T[BooleanKeys<T>]>!this.isChecked;

    if (this.args.showDisableConfirmation && !confirmed && !newToggleValue) {
      this.showConfirmationModal = true;
      return;
    }

    this.args.model[this.args.toggleKey] = newToggleValue;

    try {
      await this.args.model.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
      this.args.model.rollbackAttributes();
    }
  });

  <template>
    <SettingsPanel as |panel|>
      <panel.header
        @iconUri="/assets/images/features/badge.svg"
        @title={{@title}}
        @description={{@description}}
        as |header|
      >
        <header.controls>
          {{#if (and @cannotToggleLink @cannotToggleLinkText)}}
            <a
              data-test-rebadging-toggle={{@toggleKey}}
              class="btn btn--brand btn--small"
              href={{@cannotToggleLink}}
              target="_blank"
              rel="noopener"
            >
              {{@cannotToggleLinkText}}
            </a>
          {{else}}
            <SlideToggle
              data-test-rebadging-toggle={{@toggleKey}}
              @isDisabled={{this.toggleTask.isRunning}}
              @isChecked={{this.isChecked}}
              @onClick={{this.toggleTask.perform}}
            />
          {{/if}}
        </header.controls>
      </panel.header>
    </SettingsPanel>

    {{#if this.showConfirmationModal}}
      <ConfirmationModal
        @title="Disable property passes"
        @ctaText="Confirm"
        @onClose={{set this "showConfirmationModal" false}}
        @handleTaskAction={{fn this.toggleTask.perform true}}
      >
        <p class="mt-4">
          {{@confirmationMessage}}
        </p>
      </ConfirmationModal>
    {{/if}}
  </template>
}
